import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { FaEdit, FaSearch, FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "../../Styles/yellowcalfcss/Type.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import ScaleLoader from "react-spinners/ScaleLoader";

export default function AccountsType() {
  // States
  const [entriesList, setEntriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  // Field States
  const [nameFieldValue, setNameFieldValue] = useState("");
  const [searchFilterText, setSearchFilterText] = useState("");

  /**
   * 1. GET LIST API Integration
   */
  const fetchAccountTypes = useCallback(async () => {
    setLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")) || 1609,
      api_key:
        localStorage.getItem("api_key") || "8440d41adb8335314263dc31d4504cf7",
      user_id: Number(localStorage.getItem("user_id")) || 56950,
      language: "en",
      name_search: searchFilterText,
      limit: 1000,
      page: 1,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.expensesaccounttype,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        // Map API data to your component's expected format
        const formattedData = response.data.map((item) => ({
          identifier: item.expenses_id || item.expense_account_type_id,
          accountTypeName: item.expense_account_type_name,
        }));
        setEntriesList(formattedData);
      } else {
        setEntriesList([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load account types");
    } finally {
      setLoading(false);
    }
  }, [searchFilterText]);

  useEffect(() => {
    fetchAccountTypes();
  }, [fetchAccountTypes]);

  /**
   * 2. ADD & UPDATE API Integration
   */
  const handleConfirmSave = async () => {
    if (!nameFieldValue.trim()) {
      toast.warn("Please enter a name");
      return;
    }

    setLoading(true);
    const isUpdate = !!editingEntry;
    const url = isUpdate
      ? ApiConfig.AccountTypeUpdate
      : ApiConfig.AccountTypeAdd;

    const payload = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      type_name: nameFieldValue,
      ...(isUpdate && { id: editingEntry.identifier }), // Add ID only if updating
    };

    try {
      const response = await simplePostCall(url, JSON.stringify(payload));
      if (response?.result) {
        toast.success(
          isUpdate ? "Updated Successfully!" : "Created Successfully!",
        );
        setDialogOpen(false);
        fetchAccountTypes(); // Refresh list
      } else {
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      toast.error("Network Error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * 3. DELETE API Integration
   */
  const handlePerformDelete = async (identifier) => {
    if (!window.confirm("Are you sure you want to delete this?")) return;

    setLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: identifier,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.AccountTypeDelete,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        toast.success("Deleted successfully");
        fetchAccountTypes();
      } else {
        toast.error(response?.message || "Failed to delete");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // UI Handlers
  const handleInitiateCreate = () => {
    setEditingEntry(null);
    setNameFieldValue("");
    setDialogOpen(true);
  };

  const handleInitiateEdit = (entry) => {
    setEditingEntry(entry);
    setNameFieldValue(entry.accountTypeName);
    setDialogOpen(true);
  };

  return (
    <div className="atm-outer-wrapper">
      <ToastContainer />
      <div className="special-header panel-white-all">
        <h4 className="page-title">Account Type</h4>
        <div className="action-buttons-group">
          <button className="btn btn-primary" onClick={handleInitiateCreate}>
            Create Account Type
          </button>
          <button className="btn btn-primary">Export</button>
        </div>
      </div>

      <div className="yc-list-body">
        <div className="yc-list-filter">
          <div className="ff-search-wrap">
            <FaSearch className="ff-search-icon" />
            <input
              type="text"
              className="form-control ff-search-input"
              placeholder="Search Account Type..."
              value={searchFilterText}
              onChange={(e) => setSearchFilterText(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={() => setSearchFilterText("")}>
            Clear
          </button>
        </div>

        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-3">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Account Type Name</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {loading && entriesList.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "20vh" }}>
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : entriesList.length > 0 ? (
          <div className="yc-list-row-list">
            {entriesList.map((entry, index) => (
              <div key={entry.identifier} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-3">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{entry.accountTypeName}</div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      onClick={() => handleInitiateEdit(entry)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      onClick={() => handlePerformDelete(entry.identifier)}
                      title="Delete"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="yc-empty-state">No account types found.</div>
        )}
      </div>

      <Modal show={dialogOpen} onHide={() => setDialogOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEntry ? "Edit Account Type" : "Create Account Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Account Type Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter account type name"
                value={nameFieldValue}
                onChange={(e) => setNameFieldValue(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
